import { uiText } from '../../i18n';
import { Seg } from '../../ui/Seg';
import { StudioIcon } from './StudioIcon';
import { StudioGold } from './StudioGold';
/** Загрузка и ошибка списка. Пустую выдачу рисует StudioZero: у неё свой текст и действие на каждый случай. */
export function StudioStatus({ loading, error, retry }: { loading?: boolean; error?: boolean; retry: () => void }) {
  return (
    <>
      {loading && (
        <p role="status" className="st-status">
          {uiText('Загружаем…')}
        </p>
      )}
      {error && (
        <div role="alert" className="st-zero st-status">
          <StudioIcon />
          <p>{uiText('Не удалось загрузить студию.')}</p>
          <button type="button" className="btn line sm" onClick={retry}>
            {uiText('Обновить')}
          </button>
        </div>
      )}
    </>
  );
}
/** Скелетон ленты шаблонов вместо «Загружаем…» (владелец 02.11.2026, как у «Озвучки»): две колонки плиток 9:16 с полосками названия
    и подписи внизу; пульс — общий .skel, по плиткам с шагом 60 мс; проявляется после порога, чтобы быстрый ответ не мигал скелетоном */
export function StudioCardsSkeleton({ count = 6 }: { count?: number }) {
  const column = (offset: number) => (
    <div className="c">
      {Array.from({ length: Math.ceil(count / 2) }, (_, index) => (
        <span key={index} className="st-app st-csk">
          <span className="pic skel" style={{ aspectRatio: '9 / 16', animationDelay: `${(index * 2 + offset) * 60}ms` }}>
            <i className="t" />
            <i className="s" />
          </span>
        </span>
      ))}
    </div>
  );
  return (
    <div className="st-mas st-cskl" aria-hidden="true">
      {column(0)}
      {column(1)}
    </div>
  );
}
/** Строка вкладок страницы-хозяина, пока документ не пришёл (top_tabs приходят вместе с первой страницей): та же капсула .seg
    с пульсирующими пилюлями — лента под ней не съезжает, когда встанут настоящие вкладки */
export function StudioSegmentsSkeleton() {
  return (
    <div className="seg nosb st-tlseg st-segsk" aria-hidden="true">
      {[48, 80, 120, 104, 92].map((width, index) => (
        <i key={index} className="skel" style={{ width, animationDelay: `${index * 60}ms` }} />
      ))}
    </div>
  );
}
export function StudioSegments({
  items,
  value,
  onChange,
  counts,
  gold,
  disabled,
  sub,
  /** Простые кнопки без ролей — сегмент форматов главной (.st-fseg в appsHTML прототипа) */
  plain = false,
  className = 'st-tlseg',
}: {
  items: string[][];
  value: string;
  onChange: (value: string) => void;
  /** Коды вкладок с золотом: подпись рисует StudioGold (accent: "gold", §6.2 и §6.5) */
  gold?: Set<string>;
  counts?: Record<string, number>;
  /** Неактивные пункты, когда их считает сервер (tabs_list, docs/03-api.md §6.5) */
  disabled?: Set<string>;
  /** Вторая строка: подпись лежит в .l с data-t — невидимая полужирная копия держит ширину (bot-studio.css) */
  sub?: boolean;
  plain?: boolean;
  className?: string;
}) {
  return (
    <Seg
      items={items.map(([id, label]) => ({
        key: id,
        label: gold?.has(id) ? (
          <StudioGold text={uiText(label)} />
        ) : sub ? (
          <span className="l" data-t={uiText(label)}>
            {uiText(label)}
          </span>
        ) : (
          uiText(label)
        ),
        disabled: id !== 'all' && (disabled ? disabled.has(id) : counts !== undefined && (counts[id] ?? 0) === 0),
      }))}
      value={value}
      onChange={onChange}
      /* Вкладки каталога и строка состава — tablist с tab и aria-selected (segHTML, subRowHTML прототипа).
         Сегмент форматов главной в эталоне ролей не несёт вовсе — для него plain: Seg тогда не пишет
         ни role, ни aria-*, ни --ml/--mr (appsHTML — голый div.seg.sm.anim.st-fseg). */
      plain={plain}
      /* выбор нажатием подводит пункт плавно: segSync(id, true) у catTab, catFmt, kTab, tlSec эталона */
      smooth
      role="tablist"
      aria="aria-selected"
      /* nosb стоит у вкладок и строки состава (segHTML, subRowHTML эталона); у сегмента
         форматов главной (.st-fseg, appsHTML) его нет — он не листается */
      className={`anim${plain ? '' : ' nosb'} ${className}`}
    />
  );
}
